import { Ref } from 'vue';
import { WEBSHOP_MAX_PRODUCT_QUANTITY, WEBSHOP_MIN_PRODUCT_QUANTITY } from '@/constants/webshop';

export function useQuantityValidation(quantity?: Ref<number | null>, options?: { minValue?: number }) {
  const minValue = options?.minValue ?? WEBSHOP_MIN_PRODUCT_QUANTITY;
  const isControlKey = (key: string): boolean => key.length > 1;
  const isInvalidCharacter = (key: string): boolean => ['-', '.', 'e', '+'].includes(key);
  const isDigit = (key: string): boolean => /^[0-9]$/.test(key);

  const validateQuantityInput = (event: KeyboardEvent) => {
    const { key, target } = event;

    if (isControlKey(key)) {
      // Allow Backspace, Delete, Tab, Enter, Escape, and navigation keys
      const allowedControlKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (allowedControlKeys.includes(key)) {
        return;
      }
      // Block ArrowUp and ArrowDown (prevent number increment/decrement)
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
      }
      return;
    }

    if (isInvalidCharacter(key) || !isDigit(key)) {
      event.preventDefault();
      return;
    }

    const input = target as HTMLInputElement;
    const { value, selectionStart = 0, selectionEnd = 0 } = input;

    // Calculate what the new value would be after this keystroke
    const newValue = value.substring(0, selectionStart) + key + value.substring(selectionEnd);

    // If the new value is empty (shouldn't happen with digit key), allow it
    if (newValue === '') {
      return;
    }

    const numericValue = parseInt(newValue, 10);

    // Prevent if the new value would be greater than max (stops at 66 when typing 666)
    if (!isNaN(numericValue) && numericValue > WEBSHOP_MAX_PRODUCT_QUANTITY) {
      event.preventDefault();
      return;
    }

    // Prevent if the new value would be negative
    if (!isNaN(numericValue) && numericValue < 0) {
      event.preventDefault();
    }
  };

  const clampValue = (value: number): number =>
    Math.min(Math.max(value, minValue), WEBSHOP_MAX_PRODUCT_QUANTITY);

  const updateQuantity = (input: HTMLInputElement, value: number | null) => {
    const stringValue = value === null ? '' : value.toString();
    input.value = stringValue;
    if (quantity) {
      quantity.value = value;
    }
  };

  const handleQuantityChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    // Handle empty
    if (rawValue === '') {
      input.value = '';
      if (quantity) {
        quantity.value = null;
      }
      return;
    }

    // Remove any non-digit characters (including - sign)
    const cleanedValue = rawValue.replace(/[^0-9]/g, '');

    if (cleanedValue === '') {
      // Allow empty during editing - will be validated on blur
      input.value = '';
      if (quantity) {
        quantity.value = null;
      }
      return;
    }

    const value = parseInt(cleanedValue, 10);

    // Additional safety check for negative values
    if (isNaN(value) || value < minValue) {
      input.value = minValue.toString();
      if (quantity) {
        quantity.value = minValue;
      }
      return;
    }

    const clampedValue = clampValue(value);
    input.value = clampedValue.toString();
    if (quantity) {
      quantity.value = clampedValue;
    }
  };

  const handleQuantityBlur = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.value === '' && minValue > 0) {
      updateQuantity(input, minValue);
    }
  };

  return {
    validateQuantityInput,
    handleQuantityChange,
    handleQuantityBlur
  };
}
